import range from 'lodash/range';

import { Editor, Path, Transforms } from '@lumapps/wrex/slate';
import { getSibling } from '@lumapps/wrex/slate/utils/getSibling';
import { isElement } from '@lumapps/wrex/slate/utils/isElement';
import type { Wrex } from '@lumapps/wrex/types';

import { isList } from '../../utils/isList';
import { isListItem } from '../../utils/isListItem';
import { Normalize } from './types';

/**
 * Normalize list children (not list item).
 */
const normalizeListChildren: Normalize = (editor, [originalNode, originalPath]) => {
    // Skip if at root path or if is list item.
    if (!originalPath.length || isListItem(originalNode)) {
        return false;
    }
    const [parent] = Editor.parent(editor, originalPath);
    // Skip if parent is not a list.
    if (!isList(parent)) {
        return false;
    }

    // Fix: <ul><ul><li>...</li></ul></ul> → <ul><li><ul><li>...</li></ul></li></ul>
    // A list cannot be a direct child of another list; it must be inside a <li>. (common in GDoc pastes)
    if (isList(originalNode)) {
        const [before, beforePath] = getSibling(editor, originalPath, 'before') ?? [null, null];
        if (!before || !beforePath || !isElement(before)) {
            return false;
        }

        const indexInPrevious = before.children.length;
        Transforms.moveNodes(editor, { at: originalPath, to: [...beforePath, indexInPrevious] });
        return true;
    }

    // Fix: <ul><p>text</p></ul> → remove the <p>, only <li> children are valid in a list.
    Transforms.removeNodes(editor, { at: originalPath });
    return true;
};

/**
 * Normalize list node (ordered or unordered).
 */
export const normalizeList: Normalize = (editor, [originalNode, originalPath]) => {
    if (normalizeListChildren(editor, [originalNode, originalPath])) {
        return true;
    }
    if (!isList(originalNode)) {
        return false;
    }
    const pathRef = Editor.pathRef(editor, originalPath);
    let node = Editor.node(editor, pathRef.current as Path)[0] as Wrex.Element;

    Editor.withoutNormalizing(editor, () => {
        // Fix: <ul><li>A</li></ul><ul><li>B</li></ul> → <ul><li>A</li><li>B</li></ul>
        // Merge adjacent lists of the same type into one.
        const [before, beforePath] = getSibling(editor, pathRef.current as Path, 'before') ?? [null, null];
        if (isElement(before) && beforePath && before.type === node.type && before.children.length) {
            const path = pathRef.current as Path;
            // Iterate in reverse so that moving a child to index 0 doesn't shift the indices of the remaining children.
            for (const index of range(0, before.children.length).reverse()) {
                Transforms.moveNodes(editor, {
                    at: [...beforePath, index],
                    to: [...path, 0],
                });
            }
            Transforms.removeNodes(editor, { at: beforePath });

            node = Editor.node(editor, pathRef.current as Path)[0] as Wrex.Element;
        }

        // Same merge as above but with the following sibling.
        const [after, afterPath] = getSibling(editor, pathRef.current as Path, 'after') ?? [null, null];
        if (isElement(after) && afterPath && after.type === node.type && node.children.length) {
            const path = pathRef.current as Path;
            for (const index of range(0, node.children.length).reverse()) {
                Transforms.moveNodes(editor, {
                    at: [...path, index],
                    to: [...afterPath, 0],
                });
            }
            node = Editor.node(editor, pathRef.current as Path)[0] as Wrex.Element;
        }

        // Fix: <ul></ul> or <ul><p>text</p></ul> → removed entirely (invalid list).
        if (!node.children.length || !node.children.every(isListItem)) {
            Transforms.removeNodes(editor, { at: pathRef.current as Path });
        }

        // Fix: <div><ul><li>...</li></ul></div> → <ul><li>...</li></ul>
        // A list must be a direct child of the editor or inside a <li> (for nesting). Lift it out otherwise.
        if (pathRef.current && pathRef.current.length > 1 && !isListItem(Editor.parent(editor, pathRef.current)[0])) {
            Transforms.liftNodes(editor, {
                at: pathRef.current as Path,
                mode: 'highest',
            });
        }
    });

    pathRef.unref();
    return true;
};
